import React, { useRef, useEffect, useState } from 'react';
import maplibregl from 'maplibre-gl';
import * as turf from '@turf/turf';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { useMapStore } from '../../stores/mapStore';
import { useSelectedLayers } from '../../stores/mapStore';
import SiteAnalysisPanel from '../ui/SiteAnalysisPanel';
import type { MapConfig, LayerControlData, CommunityTree, SiteAnalysisPanelProps } from '../../types';

function mercatorToLatLon(mercatorX: number, mercatorY: number): [number, number] {
  const rMajor = 6378137; // Equatorial Radius, WGS84
  const shift = Math.PI * rMajor;
  const lon = (mercatorX / shift) * 180.0;
  let lat = (mercatorY / shift) * 180.0;
  lat = (180 / Math.PI) * (2 * Math.atan(Math.exp((lat * Math.PI) / 180.0)) - Math.PI / 2.0);
  return [lon, lat];
}

interface MapContainerProps {
  layers?: LayerControlData[];
  communityTrees?: CommunityTree[];
  onTreeDrop?: (longitude: number, latitude: number, treeData: any) => void;
  selectedAddress?: { longitude: number; latitude: number; address: string } | null;
}

export const MapContainer: React.FC<MapContainerProps> = ({ layers = [], communityTrees = [], onTreeDrop, selectedAddress }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [dataLayersLoaded, setDataLayersLoaded] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [boundaryGeometry, setBoundaryGeometry] = useState<any>(null);
  const addressMarkerRef = useRef<maplibregl.Marker | null>(null);
  const [showAssessmentPanel, setShowAssessmentPanel] = useState(false);
  const [assessmentData, setAssessmentData] = useState<SiteAnalysisPanelProps['analysisData'] | null>(null);
  const [assessmentPanelKey, setAssessmentPanelKey] = useState(0);
  const mapClickHandlerRef = useRef<((e: maplibregl.MapMouseEvent) => void) | null>(null);
  
  const { center, zoom, setCenter, setZoom, setBounds } = useMapStore();
  const selectedLayers = useSelectedLayers();

  // Function to check if a point is within Hackensack boundary using Turf.js
  const isPointInHackensackBoundary = (lng: number, lat: number): boolean => {
    if (!boundaryGeometry) {
      return false; // Don't allow planting if boundary not loaded
    }
   
    try {
      const point = turf.point([lng, lat]);
      
      // For FeatureCollection, use the first feature
      let polygon;
      if (boundaryGeometry.type === 'FeatureCollection' && boundaryGeometry.features?.length > 0) {
        polygon = boundaryGeometry.features[0];
      } else {
        polygon = boundaryGeometry;
      }
      
      // Handle MultiPolygon by checking each individual polygon
      let isInside = false;
      
      if (polygon.geometry?.type === 'MultiPolygon') {
        const multiPolygon = polygon;
        for (let i = 0; i < multiPolygon.geometry.coordinates.length; i++) {
          const singlePolygon = turf.polygon(multiPolygon.geometry.coordinates[i]);
          if (booleanPointInPolygon(point, singlePolygon)) {
            isInside = true;
            break;
          }
        }
      } else {
        isInside = booleanPointInPolygon(point, polygon);
      }
      
      return isInside;
    } catch (error) {
      return false;
    }
  };

  // Function to create and manage address marker
  const createAddressMarker = (longitude: number, latitude: number, address: string) => {
    if (!map.current) return;

    // Remove existing marker if it exists
    if (addressMarkerRef.current) {
      addressMarkerRef.current.remove();
      addressMarkerRef.current = null;
    }

    // Create custom marker element
    const markerElement = document.createElement('div');
    markerElement.className = 'address-marker';
    markerElement.innerHTML = `
      <div style="
        width: 32px;
        height: 32px;
        background: #ef4444;
        border: 3px solid #ffffff;
        border-radius: 50%;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        position: relative;
        cursor: pointer;
      ">
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 8px;
          height: 8px;
          background: #ffffff;
          border-radius: 50%;
        "></div>
      </div>
      <div style="
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-top: 8px solid #ef4444;
        margin-top: -1px;
      "></div>
    `;

    // Create marker
    const marker = new maplibregl.Marker(markerElement)
      .setLngLat([longitude, latitude])
      .addTo(map.current);

    // Create popup for the marker
    const popup = new maplibregl.Popup({ offset: 25 })
      .setHTML(`
        <div class="p-3 min-w-64">
          <h3 class="font-semibold text-lg text-red-600 mb-2">📍 Selected Address</h3>
          <p class="text-sm text-gray-700">${address}</p>
          <p class="text-xs text-gray-500 mt-2">
            Coordinates: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}
          </p>
        </div>
      `);

    marker.setPopup(popup);
    addressMarkerRef.current = marker;

    // Show popup briefly
    setTimeout(() => {
      popup.addTo(map.current!);
    }, 500);
  };

  // Function to zoom to address location
  const zoomToAddress = (longitude: number, latitude: number) => {
    if (!map.current) return;

    map.current.flyTo({
      center: [longitude, latitude],
      zoom: 17, // Close zoom level to see the specific address
      duration: 2000, // Smooth 2-second animation
      essential: true
    });
  };

  // Function to reset map to Hackensack boundary view
  const resetMapToHackensack = () => {
    if (!map.current || !boundaryGeometry) return;

    try {
      const bbox = turf.bbox(boundaryGeometry) as [number, number, number, number];
      map.current.fitBounds(bbox, { 
        padding: 50,
        duration: 2000, // Smooth 2-second animation
        essential: true
      });
    } catch (error) {
      // Fallback to default center and zoom
      map.current.flyTo({
        center: center,
        zoom: 12,
        duration: 2000,
        essential: true
      });
    }
  };

  // Function to clear address marker
  const clearAddressMarker = () => {
    if (addressMarkerRef.current) {
      addressMarkerRef.current.remove();
      addressMarkerRef.current = null;
    }
  };

  // Function to convert flood zone codes to descriptions
  const getFloodZoneDescription = (zoneCode: string): string => {
    const floodZoneMap: { [key: string]: string } = {
      'AE': 'High flood risk',
      'A': 'High flood risk',
      'AH': 'High flood risk (shallow)',
      'AO': 'High flood risk (sheet flow)',
      'X': 'Moderate flood risk',
      'X500': 'Moderate flood risk',
      'SHADED X': 'Moderate flood risk',
      'D': 'Undetermined risk',
      'VE': 'High coastal flood risk',
      'V': 'High coastal flood risk'
    };
    
    return floodZoneMap[zoneCode.toUpperCase()] || `Zone ${zoneCode}`;
  };

  // Query layers at clicked point for assessment data
  const queryLayersAtPoint = (lng: number, lat: number) => {
    if (!map.current) return null;

    // Check layer visibility
    const heatIslandsVisible = getLayerVisibility('heatIslands');
    const ejVisible = getLayerVisibility('environmentalJustice');
    const floodZonesVisible = getLayerVisibility('floodZones');
    
    const hasVisibleLayers = heatIslandsVisible || ejVisible || floodZonesVisible;
    
    if (!hasVisibleLayers) {
      return null;
    }

    const point: [number, number] = [lng, lat];
    const features = map.current.queryRenderedFeatures(map.current.project(point));
    // Initialize default values
    let temperature: string | undefined = undefined;
    let treePriority: string | undefined = undefined;
    let ejPriority: string | undefined = undefined;
    let floodRisk: string | undefined = undefined;

    // Query each visible layer for data
    features.forEach(feature => {
      const props = feature.properties;
      
      // Process heat island data
      if (feature.source === 'hackensack_heat_contours' && heatIslandsVisible) {
        if (props?.tree_priority) {
          treePriority = props.tree_priority;
        }
        
        if (props?.temperature_f) {
          temperature = `${props.temperature_f}°F`;
        }
      }
      
      // Process environmental justice data
      if (feature.source === 'hackensack_eji' && ejVisible) {
        if (props?.EJ_PRIORITY_SCORE) {
          ejPriority = `${props.EJ_PRIORITY_SCORE}`;
        }
      }
      
      // Process flood zone data
      if (feature.source === 'hackensack_nfhl' && floodZonesVisible) {
        if (props?.FLD_ZONE) {
          floodRisk = getFloodZoneDescription(props.FLD_ZONE);
          if (props?.ZONE_SUBTY) {
            floodRisk += ` (${props.ZONE_SUBTY})`;
          }
        }
      }
    });


    return {
      temperature,
      treePriority,
      ejPriority,
      floodRisk
    };
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    if (!map.current || !onTreeDrop) {
      return;
    }

    try {
      const treeDataString = e.dataTransfer.getData('text/plain');
      
      if (!treeDataString) {
        return;
      }
      
      const treeData = JSON.parse(treeDataString);
      
      // Convert screen coordinates to map coordinates
      const rect = mapContainer.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Convert pixel coordinates to longitude/latitude
      const lngLat = map.current.unproject([x, y]);
      
      // Check if the drop location is within Hackensack boundary
      if (!isPointInHackensackBoundary(lngLat.lng, lngLat.lat)) {
        // Show visual feedback to user
        const errorDiv = document.createElement('div');
        errorDiv.style.cssText = `
          position: fixed; 
          top: 20px; 
          right: 20px; 
          background: #ef4444; 
          color: white; 
          padding: 12px 24px; 
          border-radius: 8px; 
          z-index: 9999;
          font-size: 14px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        `;
        errorDiv.textContent = 'Trees can only be planted within Hackensack city limits';
        document.body.appendChild(errorDiv);
        
        // Remove after 4 seconds
        setTimeout(() => {
          if (errorDiv.parentNode) {
            errorDiv.parentNode.removeChild(errorDiv);
          }
        }, 4000);
        
        return;
      }
      
      onTreeDrop(lngLat.lng, lngLat.lat, treeData);
    } catch (error) {
      // Silent error handling
    }
  };

  // Convert layers prop to visibility map
  const getLayerVisibility = (layerId: string): boolean => {
    const layer = layers.find(l => l.id === layerId);
    // Use isVisible for actual layer visibility (controlled by eye button)
    return layer?.isVisible ?? false;
  };

  // Get layer order for proper stacking
  const getLayerOrder = (layerId: string): number => {
    const layer = layers.find(l => l.id === layerId);
    return layer?.order ?? 0;
  };

  // Get layer transparency (convert from percentage to opacity)
  const getLayerOpacity = (layerId: string): number => {
    const layer = layers.find(l => l.id === layerId);
    return layer ? (layer.transparency / 100) : 1;
  };


  // Map configuration
  const mapConfig: MapConfig = {
    center: center,
    zoom: zoom,
    minZoom: 10,
    maxZoom: 22,
    style: 'https://api.maptiler.com/maps/streets-v2/style.json?key=EMx1V5aNwnzdNhNGtGNq', 
    // maxBounds: [
    //   [
    //     parseFloat(import.meta.env.VITE_PROJECT_BOUNDS_SW_LNG || '-74.0800'),
    //     parseFloat(import.meta.env.VITE_PROJECT_BOUNDS_SW_LAT || '40.8500')
    //   ],
    //   [
    //     parseFloat(import.meta.env.VITE_PROJECT_BOUNDS_NE_LNG || '-74.0000'),
    //     parseFloat(import.meta.env.VITE_PROJECT_BOUNDS_NE_LAT || '40.9100')
    //   ]
    // ]
  };

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: mapConfig.style,
      center: mapConfig.center,
      zoom: mapConfig.zoom,
      minZoom: mapConfig.minZoom,
      maxZoom: mapConfig.maxZoom,
      maxBounds: mapConfig.maxBounds,
    });

    // Add navigation controls
    map.current.addControl(new maplibregl.NavigationControl(), 'top-right');

    // Map event listeners
      map.current.on('load', () => {
        setMapLoaded(true);
      });


    map.current.on('moveend', () => {
      if (map.current) {
        const newCenter = map.current.getCenter();
        const newZoom = map.current.getZoom();
        setCenter([newCenter.lng, newCenter.lat]);
        setZoom(newZoom);
        
        const bounds = map.current.getBounds();
        setBounds([
          [bounds.getWest(), bounds.getSouth()],
          [bounds.getEast(), bounds.getNorth()]
        ]);
      }
    });

    return () => {
      // Clean up address marker
      if (addressMarkerRef.current) {
        addressMarkerRef.current.remove();
        addressMarkerRef.current = null;
      }
      
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Load GeoJSON data layers with proper error handling and ordering
  const loadDataLayers = async () => {
    if (!map.current || dataLayersLoaded) return;

    let bbox: [number, number, number, number] | undefined;

    try {
      // Load Hackensack boundary first to get bbox
      try {
        const boundaryResponse = await fetch('/data/hackensack_boundary.geojson');
        if (!boundaryResponse.ok) {
          console.warn('Boundary data not found, skipping layer');
        } else {
          const boundaryData = await boundaryResponse.json();
          
          // Store boundary geometry for point-in-polygon checks
          setBoundaryGeometry(boundaryData);
          bbox = turf.bbox(boundaryData) as [number, number, number, number];
          map.current.fitBounds(bbox, { padding: 20 });
        }
      } catch (boundaryError) {
        console.warn('Failed to load boundary layer:', boundaryError);
      }

      // Define layer configurations
      const layerConfigs = [
        {
          id: 'heatIslands',
          url: '/data/hackensack_heat_contours.geojson',
          sourceId: 'hackensack_heat_contours',
          layerId: 'hackensack_heat_contours-fill',
          type: 'fill' as const,
          paint: {
            'fill-color': [
              'match',
              ['get', 'tree_priority'],
              'high', '#DC2626',
              'medium', '#F59E0B',
              'rgba(0,0,0,0)'
            ] as any,
            'fill-opacity': getLayerOpacity('heatIslands')
          }
        },
        {
          id: 'floodZones',
          url: '/data/hackensack_nfhl.geojson',
          sourceId: 'hackensack_nfhl',
          layerId: 'hackensack_nfhl-fill',
          type: 'fill' as const,
          paint: {
            'fill-color': [
              'match',
              ['get', 'FLD_ZONE'],
              'X', '#1E40AF',
              'AE', '#3B82F6',
              '#93C5FD'
            ] as any,
            'fill-opacity': getLayerOpacity('floodZones')
          }
        },
        {
          id: 'environmentalJustice',
          url: '/data/hackensack_eji.geojson',
          sourceId: 'hackensack_eji',
          layerId: 'hackensack_eji-fill',
          type: 'fill' as const,
          paint: {
            'fill-color': [
              'step',
              ['get', 'EJ_PRIORITY_SCORE'],
              'rgba(0,0,0,0)',
              6.4, '#A855F7',
              6.55, '#9333EA',
              7.0, '#4C1D95'
            ] as any,
            'fill-opacity': getLayerOpacity('environmentalJustice'),
            'fill-outline-color': '#1F2937'
          }
        },
      ];

      // Sort layers by their order in the layers prop, but only include layers that exist in the layers prop
      const orderedConfigs = layerConfigs
        .filter(config => layers.some(layer => layer.id === config.id))
        .map(config => ({
          ...config,
          order: getLayerOrder(config.id)
        }))
        .sort((a, b) => a.order - b.order);

      // Load layers in order
      for (const config of orderedConfigs) {
        try {
            // Handle vector layer
            const response = await fetch(config.url);
            if (!response.ok) {
              console.warn(`${config.id} data not found, skipping layer`);
              continue;
            }
            const data = await response.json();
            
            map.current.addSource(config.sourceId, {
              type: 'geojson',
              data: data
            });


          map.current.addLayer({
            id: config.layerId,
            type: config.type,
            source: config.sourceId,
            paint: config.paint,
            layout: {
              visibility: getLayerVisibility(config.id) ? 'visible' : 'none'
            }
          });
        } catch (error) {
          console.warn(`Failed to load ${config.id} layer:`, error);
        }
      }

      // Add boundary layer using already loaded boundary data
      if (boundaryGeometry) {
        try {
          map.current.addSource('hackensack_boundary', {
            type: 'geojson',
            data: boundaryGeometry
          });

          map.current.addLayer({
            id: 'hackensack_boundary-line',
            type: 'line',
            source: 'hackensack_boundary',
            paint: {
              'line-color': '#000000',
              'line-width': 2
            },
            layout: {
              visibility: selectedLayers.hackensackBoundary ? 'visible' : 'none'
            }
          });
        } catch (boundaryError) {
          console.warn('Failed to add boundary layer:', boundaryError);
        }
      }


      // Mark data layers as loaded
      setDataLayersLoaded(true);

    } catch (error) {
      console.error('Error loading map layers:', error);
    }
  };

  // Function to load community trees data
  const loadCommunityTrees = () => {
    if (!map.current || communityTrees.length === 0) {
      return;
    }

    try {
      // Create GeoJSON from community trees data
      const geojsonData = {
        type: 'FeatureCollection' as const,
        features: communityTrees.map((tree) => ({
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: [tree.longitude, tree.latitude]
          },
          properties: {
            id: tree.id,
            species_id: tree.species_id,
            species_name: tree.species_name,
            species_scientific_name: tree.species_scientific_name,
            address: tree.address,
            neighborhood: tree.neighborhood,
            current_status: tree.current_status,
            planting_date: tree.planting_date,
            site_assessment_score: tree.site_assessment_score,
            ownership_type: tree.ownership_type
          }
        }))
      };

      // Remove existing source and layer if they exist
      if (map.current.getLayer('community-trees-layer')) {
        map.current.removeLayer('community-trees-layer');
      }
      if (map.current.getSource('community-trees')) {
        map.current.removeSource('community-trees');
      }

      // Add source
      map.current.addSource('community-trees', {
        type: 'geojson',
        data: geojsonData
      });

      // Add community trees layer as circles
      map.current.addLayer({
        id: 'community-trees-layer',
        type: 'circle',
        source: 'community-trees',
        paint: {
          'circle-radius': 10,
          'circle-color': '#22c55e',
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 3,
          'circle-opacity': 1
        },
        layout: {
          visibility:  getLayerVisibility('communityTrees') ? 'visible' : 'none' 
        }
      });

      // Community trees loaded without auto-zoom

      // Add click event for tree popups
      map.current.on('click', 'community-trees-layer', (e) => {
        if (e.features && e.features[0]) {
          const feature = e.features[0];
          const coordinates = (feature.geometry as any).coordinates.slice();
          const properties = feature.properties;

          new maplibregl.Popup()
            .setLngLat(coordinates)
            .setHTML(`
              <div class="p-3 min-w-64" style="color: #000000;">
                <h3 class="font-semibold text-lg text-green-700">${properties?.species_name || `Community Tree #${properties?.id}`}</h3>
                ${properties?.species_scientific_name ? `<p class="text-sm italic text-gray-600 mb-2">${properties.species_scientific_name}</p>` : ''}
                <div class="space-y-1 text-sm" style="color: #000000;">
                  <p style="color: #000000;"><strong>Address:</strong> ${properties?.address}</p>
                  <p style="color: #000000;"><strong>Neighborhood:</strong> ${properties?.neighborhood}</p>
                  <p style="color: #000000;"><strong>Status:</strong> <span class="capitalize">${properties?.current_status}</span></p>
                  <p style="color: #000000;"><strong>Ownership:</strong> <span class="capitalize">${properties?.ownership_type}</span></p>
                  <p style="color: #000000;"><strong>Site Score:</strong> ${properties?.site_assessment_score}/100</p>
                  <p style="color: #000000;"><strong>Planted:</strong> ${new Date(properties?.planting_date).toLocaleDateString()}</p>
                </div>
              </div>
            `)
            .addTo(map.current!);
        }
      });

      // Change cursor on hover
      map.current.on('mouseenter', 'community-trees-layer', () => {
        if (map.current) {
          map.current.getCanvas().style.cursor = 'pointer';
        }
      });

      map.current.on('mouseleave', 'community-trees-layer', () => {
        if (map.current) {
          map.current.getCanvas().style.cursor = '';
        }
      });

      // Reposition the community trees layer to maintain proper layer order
      repositionCommunityTreesLayer();

    } catch (error) {
      console.error('Error loading community trees:', error);
    }
  };

  // Function to reposition community trees layer to its correct order
  const repositionCommunityTreesLayer = () => {
    if (!map.current || !map.current.getLayer('community-trees-layer')) return;
    
    // Get all layer mappings including community trees
    const allLayerMappings = [
      { id: 'heatIslands', mapLayerId: 'hackensack_heat_contours-fill' },
      { id: 'floodZones', mapLayerId: 'hackensack_nfhl-fill' },
      { id: 'environmentalJustice', mapLayerId: 'hackensack_eji-fill' },
      { id: 'communityTrees', mapLayerId: 'community-trees-layer' }
    ];

    // Get sorted layers by order
    const sortedMappings = allLayerMappings
      .filter(mapping => layers.some(layer => layer.id === mapping.id))
      .map(mapping => ({
        ...mapping,
        order: getLayerOrder(mapping.id)
      }))
      .sort((a, b) => a.order - b.order);

    // Find the community trees position in the sorted list
    const communityTreesIndex = sortedMappings.findIndex(mapping => mapping.id === 'communityTrees');
    
    if (communityTreesIndex > 0) {
      // Move community trees layer after the previous layer in the order
      const previousMapping = sortedMappings[communityTreesIndex - 1];
      if (map.current.getLayer(previousMapping.mapLayerId)) {
        try {
          map.current.moveLayer('community-trees-layer', previousMapping.mapLayerId);
        } catch (error) {
          console.warn('Failed to reposition community trees layer:', error);
        }
      }
    } else if (communityTreesIndex === 0) {
      // Move to the bottom if it's the first layer
      try {
        map.current.moveLayer('community-trees-layer');
      } catch (error) {
        console.warn('Failed to move community trees layer to bottom:', error);
      }
    }
  };

  // Function to reorder layers on the map based on their order property
  const reorderMapLayers = () => {
    if (!map.current || !mapLoaded || !dataLayersLoaded) return;

    const layerMappings = [
      { id: 'heatIslands', mapLayerId: 'hackensack_heat_contours-fill' },
      { id: 'floodZones', mapLayerId: 'hackensack_nfhl-fill' },
      { id: 'environmentalJustice', mapLayerId: 'hackensack_eji-fill' },
      { id: 'communityTrees', mapLayerId: 'community-trees-layer' }
    ];

    // Get sorted layers by order (lowest order number = bottom of stack)
    const sortedMappings = layerMappings
      .filter(mapping => layers.some(layer => layer.id === mapping.id))
      .map(mapping => ({
        ...mapping,
        order: getLayerOrder(mapping.id)
      }))
      .sort((a, b) => a.order - b.order);

    // Move layers to achieve correct stacking order
    // In MapLibre, layers added later appear on top, so we move from bottom to top
    for (let i = 0; i < sortedMappings.length; i++) {
      const mapping = sortedMappings[i];
      if (map.current.getLayer(mapping.mapLayerId)) {
        try {
          // Move layer to top (no beforeId parameter means move to top)
          // But we move them in order so the last one moved will be on top
          if (i === 0) {
            // First layer goes to the bottom (move before all other layers)
            map.current.moveLayer(mapping.mapLayerId);
          } else {
            // Subsequent layers go after the previous one
            const previousMapping = sortedMappings[i - 1];
            map.current.moveLayer(mapping.mapLayerId, previousMapping.mapLayerId);
          }
        } catch (error) {
          console.warn(`Failed to reorder layer ${mapping.mapLayerId}:`, error);
        }
      }
    }
  };

  // Helper function to get filter expression for heat islands
  const getHeatIslandsFilter = () => {
    const heatIslandsLayer = layers.find(layer => layer.id === 'heatIslands');
    const primaryFilter = heatIslandsLayer?.filters?.primary;
    
    if (!primaryFilter || primaryFilter === 'All temperatures') {
      return ['match', ['get', 'tree_priority'], 'high', '#DC2626', 'medium', '#F59E0B', 'rgba(0,0,0,0)'];
    }
    
    if (primaryFilter === 'Medium priority (+3.6°F)') {
      return ['match', ['get', 'tree_priority'], 'medium', '#F59E0B', 'rgba(0,0,0,0)'];
    }
    
    if (primaryFilter === 'High priority (+7.2°F)') {
      return ['match', ['get', 'tree_priority'], 'high', '#DC2626', 'rgba(0,0,0,0)'];
    }
    
    return ['match', ['get', 'tree_priority'], 'high', '#DC2626', 'medium', '#F59E0B', 'rgba(0,0,0,0)'];
  };

  // Helper function to get filter expression for flood zones
  const getFloodZonesFilter = () => {
    const floodZonesLayer = layers.find(layer => layer.id === 'floodZones');
    const primaryFilter = floodZonesLayer?.filters?.primary;
    
    if (!primaryFilter || primaryFilter === 'All flood zones') {
      return ['match', ['get', 'FLD_ZONE'], 'X', '#1E40AF', 'AE', '#3B82F6', '#93C5FD'];
    }
    
    if (primaryFilter === 'Zone AE (High risk)') {
      return ['match', ['get', 'FLD_ZONE'], 'AE', '#3B82F6', 'rgba(0,0,0,0)'];
    }
    
    if (primaryFilter === 'Zone X (Moderate risk)') {
      return ['match', ['get', 'FLD_ZONE'], 'X', '#1E40AF', 'rgba(0,0,0,0)'];
    }
    
    if (primaryFilter === 'Zone A (Undetermined)') {
      return ['match', ['get', 'FLD_ZONE'], 'A', '#93C5FD', 'rgba(0,0,0,0)'];
    }
    
    return ['match', ['get', 'FLD_ZONE'], 'X', '#1E40AF', 'AE', '#3B82F6', '#93C5FD'];
  };

  // Helper function to get filter expression for environmental justice
  const getEnvironmentalJusticeFilter = (): any => {
    const ejLayer = layers.find(layer => layer.id === 'environmentalJustice');
    const primaryFilter = ejLayer?.filters?.primary;
    
    if (!primaryFilter || primaryFilter === 'All EJ scores') {
      return [
        'step',
        ['get', 'EJ_PRIORITY_SCORE'],
        'rgba(0,0,0,0)',
        6.4, '#A855F7',
        6.55, '#9333EA',
        7.0, '#4C1D95'
      ];
    }
    
    if (primaryFilter === 'Low (6.4-6.55)') {
      return [
        'step',
        ['get', 'EJ_PRIORITY_SCORE'],
        'rgba(0,0,0,0)',
        6.4, '#A855F7',
        6.55, 'rgba(0,0,0,0)'
      ];
    }
    
    if (primaryFilter === 'Medium (6.55-7.0)') {
      return [
        'step',
        ['get', 'EJ_PRIORITY_SCORE'],
        'rgba(0,0,0,0)',
        6.55, '#9333EA',
        7.0, 'rgba(0,0,0,0)'
      ];
    }
    
    if (primaryFilter === 'High (>7.0)') {
      return [
        'step',
        ['get', 'EJ_PRIORITY_SCORE'],
        'rgba(0,0,0,0)',
        7.0, '#4C1D95'
      ];
    }
    
    return [
      'step',
      ['get', 'EJ_PRIORITY_SCORE'],
      'rgba(0,0,0,0)',
      6.4, '#A855F7',
      6.55, '#9333EA',
      7.0, '#4C1D95'
    ];
  };

  // Update layer visibility, transparency, filters and order when layers prop changes
  useEffect(() => {
    if (!map.current || !mapLoaded || !dataLayersLoaded) return;

    // Update visibility, transparency and filters for heat islands
    if (map.current.getLayer('hackensack_heat_contours-fill')) {
      const visibility = getLayerVisibility('heatIslands') ? 'visible' : 'none';
      const opacity = getLayerOpacity('heatIslands');
      const fillColor = getHeatIslandsFilter();
      
      map.current.setLayoutProperty(
        'hackensack_heat_contours-fill',
        'visibility',
        visibility
      );
      map.current.setPaintProperty(
        'hackensack_heat_contours-fill',
        'fill-opacity',
        opacity
      );
      map.current.setPaintProperty(
        'hackensack_heat_contours-fill',
        'fill-color',
        fillColor
      );
    }

    // Update visibility, transparency and filters for flood zones
    if (map.current.getLayer('hackensack_nfhl-fill')) {
      const visibility = getLayerVisibility('floodZones') ? 'visible' : 'none';
      const opacity = getLayerOpacity('floodZones');
      const fillColor = getFloodZonesFilter();
      
      map.current.setLayoutProperty(
        'hackensack_nfhl-fill',
        'visibility',
        visibility
      );
      map.current.setPaintProperty(
        'hackensack_nfhl-fill',
        'fill-opacity',
        opacity
      );
      map.current.setPaintProperty(
        'hackensack_nfhl-fill',
        'fill-color',
        fillColor
      );
    }

    // Update visibility, transparency and filters for environmental justice
    if (map.current.getLayer('hackensack_eji-fill')) {
      const visibility = getLayerVisibility('environmentalJustice') ? 'visible' : 'none';
      const opacity = getLayerOpacity('environmentalJustice');
      const fillColor = getEnvironmentalJusticeFilter();
      
      map.current.setLayoutProperty(
        'hackensack_eji-fill',
        'visibility',
        visibility
      );
      map.current.setPaintProperty(
        'hackensack_eji-fill',
        'fill-opacity',
        opacity
      );
      map.current.setPaintProperty(
        'hackensack_eji-fill',
        'fill-color',
        fillColor
      );
    }

    if (map.current.getLayer('hackensack_boundary-line')) {
      map.current.setLayoutProperty(
        'hackensack_boundary-line',
        'visibility',
        selectedLayers.hackensackBoundary ? 'visible' : 'none'
      );
    }

    if (map.current.getLayer('community-trees-layer')) {
      map.current.setLayoutProperty(
        'community-trees-layer',
        'visibility',
        getLayerVisibility('communityTrees') ? 'visible' : 'none'
      );
    }

    // Reorder layers based on their order property
    reorderMapLayers();
  }, [layers, mapLoaded, dataLayersLoaded]);

  // Load data layers when map is loaded and layers prop is available (only once)
  useEffect(() => {
    if (mapLoaded && layers.length > 0 && !dataLayersLoaded) {
      loadDataLayers();
    }
  }, [mapLoaded, layers.length, dataLayersLoaded]);

  // Apply initial layer ordering after data layers are loaded
  useEffect(() => {
    if (dataLayersLoaded) {
      // Small delay to ensure all layers are properly added to the map
      setTimeout(() => {
        reorderMapLayers();
      }, 50);
    }
  }, [dataLayersLoaded]);

  // Load community trees when data changes
  useEffect(() => {
    if (mapLoaded && communityTrees.length > 0) {
      loadCommunityTrees();
    }
  }, [communityTrees, mapLoaded]);

  // Handle selected address changes
  useEffect(() => {
    if (!mapLoaded) return;

    // If selectedAddress is null, clear the marker and reset map
    if (!selectedAddress) {
      clearAddressMarker();
      resetMapToHackensack();
      return;
    }

    const { longitude, latitude, address } = selectedAddress;

    // Check if the address is within Hackensack boundary
    if (!isPointInHackensackBoundary(longitude, latitude)) {
      // Show warning message that the address is outside Hackensack
      const warningDiv = document.createElement('div');
      warningDiv.style.cssText = `
        position: fixed; 
        top: 20px; 
        right: 20px; 
        background: #f59e0b; 
        color: white; 
        padding: 12px 24px; 
        border-radius: 8px; 
        z-index: 9999;
        font-size: 14px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        max-width: 300px;
      `;
      warningDiv.textContent = `Address "${address}" is outside Hackensack city limits`;
      document.body.appendChild(warningDiv);
      
      // Remove after 5 seconds
      setTimeout(() => {
        if (warningDiv.parentNode) {
          warningDiv.parentNode.removeChild(warningDiv);
        }
      }, 5000);
      
      return;
    }

    // Address is within Hackensack - create marker and zoom
    createAddressMarker(longitude, latitude, address);
    zoomToAddress(longitude, latitude);

    // Show success message
    const successDiv = document.createElement('div');
    successDiv.style.cssText = `
      position: fixed; 
      top: 20px; 
      right: 20px; 
      background: #10b981; 
      color: white; 
      padding: 12px 24px; 
      border-radius: 8px; 
      z-index: 9999;
      font-size: 14px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      max-width: 300px;
    `;
    successDiv.textContent = `Found address in Hackensack: ${address}`;
    document.body.appendChild(successDiv);
    
    // Remove after 3 seconds
    setTimeout(() => {
      if (successDiv.parentNode) {
        successDiv.parentNode.removeChild(successDiv);
      }
    }, 3000);

  }, [selectedAddress, mapLoaded, boundaryGeometry]);


  // Setup map click handler for assessment after boundary is loaded
  useEffect(() => {
    if (!map.current || !mapLoaded || !boundaryGeometry) return;

    // Remove any existing click handler first
    if (mapClickHandlerRef.current && map.current) {
      map.current.off('click', mapClickHandlerRef.current);
    }

    const handleMapClick = (e: maplibregl.MapMouseEvent) => {
      const { lng, lat } = e.lngLat;
      
      // Always clear the previous panel first
      setShowAssessmentPanel(false);
      setAssessmentData(null);
      
      // Check if click is within Hackensack boundary
      if (isPointInHackensackBoundary(lng, lat)) {
        // Query layers at the clicked point
        const layerData = queryLayersAtPoint(lng, lat);
        
        if (layerData) {
          // Use setTimeout to ensure the previous panel is cleared before showing new one
          setTimeout(() => {
            setAssessmentData(layerData);
            setShowAssessmentPanel(true);
            setAssessmentPanelKey(prev => prev + 1);
          }, 10);
        }
      }
    };
    
    // Store the handler reference
    mapClickHandlerRef.current = handleMapClick;
    
    // Add click handler
    map.current.on('click', handleMapClick);

    // Cleanup function to remove the event listener
    return () => {
      if (map.current && mapClickHandlerRef.current) {
        map.current.off('click', mapClickHandlerRef.current);
      }
    };
  }, [mapLoaded, boundaryGeometry, dataLayersLoaded]);

  return (
    <div 
      className={`relative w-full h-full ${isDragOver ? 'bg-green-100 bg-opacity-20' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{ zIndex: 1 }}
    >
      {/* Map placeholder with mockup-style background when map is loading or not available */}
      <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-slate-900 via-purple-900 to-blue-900"></div>
      
      <div ref={mapContainer} className="w-full h-full relative z-10" />
      
      {/* Site Assessment Panel */}
      {showAssessmentPanel && assessmentData && (
        <div style={{ position: 'relative', zIndex: 3000 }}>
          <SiteAnalysisPanel
            key={`assessment-${assessmentPanelKey}`}
            analysisData={assessmentData}
          />
        </div>
      )}
      
      {/* Loading indicator */}
      {!mapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 mx-auto mb-2 border-2 border-green-400 border-t-transparent rounded-full"></div>
            <p className="text-sm text-green-400">Loading map...</p>
          </div>
        </div>
      )}
    </div>
  );
};
